import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import { fetchWithAuth } from "../hooks/useAuth";

export interface IpBlacklistItem {
  ip: string;
  isBlacklisted: boolean;
  reason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface IpBlacklistResponse {
  items: IpBlacklistItem[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

type ListParams = { page: number; limit: number; search?: string };

interface IpBlacklistContextValue {
  getList: (params: ListParams) => Promise<IpBlacklistResponse>;
  add: (ip: string, reason?: string) => Promise<void>;
  remove: (ip: string, reason?: string) => Promise<void>;
  bulkAdd: (
    ips: string[],
    reason?: string
  ) => Promise<{ added: number; alreadyPresent: number; invalid: number }>;
  invalidate: () => void;
}

const IpBlacklistContext = createContext<IpBlacklistContextValue | null>(null);

const TTL_MS = 5 * 60 * 1000;

export function IpBlacklistProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const cacheRef = useRef<
    Map<string, { data: IpBlacklistResponse; timestamp: number }>
  >(new Map());

  const makeKey = (params: ListParams) => {
    const { page, limit, search = "" } = params;
    return `page=${page}|limit=${limit}|search=${search}`;
  };

  const getList = useCallback(async (params: ListParams) => {
    const key = makeKey(params);
    const now = Date.now();
    const cached = cacheRef.current.get(key);
    if (cached && now - cached.timestamp < TTL_MS) {
      return cached.data;
    }

    const qs = new URLSearchParams();
    if (params.search) qs.append("search", params.search);
    qs.append("page", String(params.page));
    qs.append("limit", String(params.limit));

    const response = await fetchWithAuth(
      `/api/admin/blacklist/ip?${qs.toString()}`
    );
    if (!response.ok) {
      throw new Error("Eroare la încărcarea blacklist-ului de IP-uri");
    }
    const data: IpBlacklistResponse = await response.json();
    cacheRef.current.set(key, { data, timestamp: now });
    return data;
  }, []);

  const invalidate = useCallback(() => {
    cacheRef.current.clear();
  }, []);

  const add = useCallback(
    async (ip: string, reason?: string) => {
      const response = await fetchWithAuth(`/api/admin/blacklist/ip/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ip, reason }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || err.message || "Eroare la adăugare IP");
      }
      invalidate();
    },
    [invalidate]
  );

  const remove = useCallback(
    async (ip: string, reason?: string) => {
      const response = await fetchWithAuth(`/api/admin/blacklist/ip/remove`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ip, reason }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || err.message || "Eroare la eliminare IP");
      }
      invalidate();
    },
    [invalidate]
  );

  const bulkAdd = useCallback(
    async (ips: string[], reason?: string) => {
      const response = await fetchWithAuth(`/api/admin/blacklist/ip/bulk-add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ips, reason }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(
          err.error || err.message || "Eroare la importul IP-urilor din Excel"
        );
      }
      const data = await response.json();
      invalidate();
      return {
        added: data.added ?? 0,
        alreadyPresent: data.alreadyPresent ?? 0,
        invalid: data.invalid ?? 0,
      };
    },
    [invalidate]
  );

  const value = useMemo<IpBlacklistContextValue>(
    () => ({ getList, add, remove, bulkAdd, invalidate }),
    [getList, add, remove, bulkAdd, invalidate]
  );

  return React.createElement(IpBlacklistContext.Provider, {
    value,
    children,
  });
}

export function useIpBlacklist(): IpBlacklistContextValue {
  const ctx = useContext(IpBlacklistContext);
  if (!ctx)
    throw new Error("useIpBlacklist trebuie folosit în IpBlacklistProvider");
  return ctx;
}
